// Extend Window interface for custom properties
declare global {
  interface Window {
    __API_FETCH_PATCHED__?: boolean;
    __API_BASE_URL__?: string;
  }
}

const LOCAL_BACKEND_ORIGIN = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
const STORAGE_KEY = '__api_base_override';

const normalizePath = (suffix) => {
  if (!suffix) {
    return '/';
  }
  return suffix.startsWith('/') ? suffix : `/${suffix}`;
};

const sanitizeOrigin = (value) => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.origin;
  } catch {
    return null;
  }
};

const readOverride = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const explicit = sanitizeOrigin(params.get('api'));
    if (explicit) {
      window.sessionStorage?.setItem(STORAGE_KEY, explicit);
      return explicit;
    }

    const stored = sanitizeOrigin(window.sessionStorage?.getItem(STORAGE_KEY));
    if (stored) {
      return stored;
    }
  } catch {}

  return null;
};

export const getApiBaseUrl = () => {
  if (typeof window === 'undefined') {
    return LOCAL_BACKEND_ORIGIN;
  }

  const override = readOverride();
  if (override) {
    return override;
  }

  const env = process.env.REACT_APP_API_BASE_URL;
  if (env && env.trim()) {
    return env.trim().replace(/\/$/, '');
  }

  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return LOCAL_BACKEND_ORIGIN;
  }

  return '';
};

export const apiUrl = (path) => {
  const base = getApiBaseUrl();
  const normalized = normalizePath(path || '/');
  return base ? `${base}${normalized}` : normalized;
};

export const configureApiFetch = () => {
  if (typeof window === 'undefined' || window.__API_FETCH_PATCHED__) {
    return;
  }

  const base = getApiBaseUrl();
  const originalFetch = window.fetch.bind(window);

  const rewrite = (url) => {
    if (typeof url !== 'string') {
      return url;
    }
    if (!url.startsWith(LOCAL_BACKEND_ORIGIN)) {
      return url;
    }
    const suffix = normalizePath(url.slice(LOCAL_BACKEND_ORIGIN.length));
    if (!base) {
      return suffix;
    }
    return `${base}${suffix}`;
  };

  window.fetch = (input, init) => {
    const apiBase = base || window.location.origin;
    const url = typeof input === 'string' ? input : '';
    if (url.startsWith(apiBase) || url.startsWith('/api/')) {
      try {
        const raw = sessionStorage.getItem('session') || localStorage.getItem('session');
        if (raw) {
          const sess = JSON.parse(raw);
          if (sess?.user?.user_id) {
            const headers = new Headers((init as RequestInit)?.headers);
            if (!headers.has('X-User-ID')) {
              headers.set('X-User-ID', String(sess.user.user_id));
            }
            init = { ...init, headers };
          }
        }
      } catch {}
    }

    const fetchPromise = typeof input === 'string'
      ? originalFetch(rewrite(input), init)
      : originalFetch(input, init);

    return fetchPromise.then((response) => {
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('session-expired'));
      }
      return response;
    });
  };

  window.__API_FETCH_PATCHED__ = true;
  window.__API_BASE_URL__ = base;
};

export const apiFetch = (path, options) => fetch(apiUrl(path), options);
